import { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { ArrowDownIcon, ArrowUpIcon, MinusIcon } from '@phosphor-icons/react';

interface DeltaTagProps {
  /** Current value */
  now: number;
  /** Baseline value to compare against */
  prev: number;
  /** Baseline label, e.g. "V2" */
  baselineLabel: string;
  /**
   * If true, a decrease is shown as positive (green) — used for Gaps / Missing Documents
   * where lower is better.
   */
  invertColor?: boolean;
}

type Tone = 'up' | 'down' | 'flat';

export const DeltaTag: FC<DeltaTagProps> = observer(({ now, prev, baselineLabel, invertColor = false }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const diff = Math.round(now - prev);
  const absDiff = Math.abs(diff);

  let tone: Tone;
  if (absDiff < 1) {
    tone = 'flat';
  } else if (invertColor) {
    tone = diff < 0 ? 'up' : 'down';
  } else {
    tone = diff > 0 ? 'up' : 'down';
  }

  const palette = {
    up: { bg: '#E8F8EF', color: theme.palette.green[700], border: '#BFE8CF' },
    down: { bg: '#FFE9E7', color: theme.palette.error.main, border: '#FAC8C3' },
    flat: { bg: '#F5F5F5', color: theme.palette.text.tertiary, border: theme.palette.grey[300] },
  }[tone];

  const sign = diff > 0 ? '+' : '';
  // Always include the baseline value so the pill conveys the actual numbers
  // being compared (not just the delta) — otherwise the flat case renders as
  // "— vs V4" which reads like missing data.
  const label =
    tone === 'flat'
      ? t('assessments.overview.versioning.kpi.deltaFlat', {
          baseline: baselineLabel,
          baselineValue: prev,
        })
      : t('assessments.overview.versioning.kpi.deltaVs', {
          sign,
          value: diff,
          baseline: baselineLabel,
          baselineValue: prev,
        });

  return (
    <Box
      sx={{
        // `alignSelf` keeps the pill content-sized when this component sits
        // inside a flex-column parent (default `alignItems: stretch` would
        // otherwise blow it out to fill the column width).
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.875,
        py: 0.25,
        borderRadius: 999,
        backgroundColor: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.color,
        fontWeight: 600,
        fontSize: 11.5,
        lineHeight: 1,
        verticalAlign: 'middle',
      }}
    >
      {tone === 'up' && <ArrowUpIcon size={11} weight="bold" />}
      {tone === 'down' && <ArrowDownIcon size={11} weight="bold" />}
      {tone === 'flat' && <MinusIcon size={11} weight="bold" />}
      <Typography component="span" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
});
